import { getBrowserTrustedDeviceFailureKind } from "../../../core/account/services/browser-trusted-device-error.ts";
import type { LoginDiagnosticStage } from "./login-diagnostics";
import type { BrowserHelpReason } from "./trusted-device-fallback.ts";

const TRUSTED_DEVICE_TIMEOUT_NAME = "TrustedDeviceTimeoutError";
const MAX_ERROR_CAUSE_DEPTH = 5;

/**
 * Browser features the trusted-device proof depends on, reported as booleans.
 *
 * @example
 * const capabilities = getTrustedDeviceCapabilities();
 */
export type TrustedDeviceCapabilities = {
  secureContext: boolean;
  webCrypto: boolean;
  indexedDb: boolean;
};

/**
 * How far the backend challenge round-trip progressed before a failure.
 *
 * @example
 * let challengeState: TrustedDeviceChallengeState = "notRequested";
 */
export type TrustedDeviceChallengeState =
  | "notRequested"
  | "requested"
  | "completed";

/**
 * Part of the preparation a bounded wait expired in.
 *
 * `local` is IndexedDB, key generation and signing on this device; `challenge`
 * is the backend round-trip, which legitimately takes longer.
 *
 * @example
 * const phase: TrustedDeviceTimeoutPhase = "challenge";
 */
export type TrustedDeviceTimeoutPhase = "local" | "challenge";

type TrustedDeviceTimeoutError = Error & {
  phase?: TrustedDeviceTimeoutPhase;
};

/**
 * Failure raised while preparing a trusted-device proof, tagged with its stage.
 *
 * **Role.** Carries the login step that broke — capability check, local
 * database, backend challenge, or signing — so a caller can report an
 * actionable diagnostic without inspecting message text.
 *
 * **Responsibility.**
 * - Owns: the failing stage and a developer-facing technical message.
 * - Does NOT own: the user-facing wording (login callers show a localized
 *   generic failure) or logging (the caller emits the diagnostic).
 *
 * @example
 * throw new TrustedDevicePreparationError(
 *   "Trusted-device challenge failed",
 *   "trustedDeviceChallenge",
 * );
 */
export class TrustedDevicePreparationError extends Error {
  public readonly stage: LoginDiagnosticStage;

  /**
   * Set only when every rung of the login ladder failed, to pick specific
   * advice (open in Chrome, leave private mode, use HTTPS).
   */
  public readonly browserHelp?: BrowserHelpReason;

  public constructor(
    message: string,
    stage: LoginDiagnosticStage,
    options?: ErrorOptions & { browserHelp?: BrowserHelpReason },
  ) {
    super(message, options);

    this.name = "TrustedDevicePreparationError";
    this.stage = stage;
    this.browserHelp = options?.browserHelp;
  }
}

/**
 * Return the advice reason a fully failed ladder attached, if any.
 *
 * @example
 * const reason = getTrustedDeviceBrowserHelp(error); // "inAppBrowser" | undefined
 */
export function getTrustedDeviceBrowserHelp(
  error: unknown,
): BrowserHelpReason | undefined {
  if (error instanceof TrustedDevicePreparationError) return error.browserHelp;

  return undefined;
}

/**
 * Probe Web Crypto and IndexedDB availability without touching either store.
 *
 * @example
 * const capabilities = getTrustedDeviceCapabilities();
 * assertTrustedDeviceCapabilities(capabilities);
 */
export function getTrustedDeviceCapabilities(): TrustedDeviceCapabilities {
  const browserWindow = typeof window === "undefined" ? undefined : window;

  return {
    secureContext: browserWindow?.isSecureContext === true,
    webCrypto: typeof browserWindow?.crypto?.subtle?.generateKey === "function",
    indexedDb: typeof browserWindow?.indexedDB?.open === "function",
  };
}

/**
 * Fail before any storage work when the browser cannot produce a proof at all.
 *
 * @remarks
 * The backend requires a trusted-device proof, so an unsupported browser must
 * stop the login instead of silently submitting without one.
 *
 * @example
 * assertTrustedDeviceCapabilities(getTrustedDeviceCapabilities());
 */
export function assertTrustedDeviceCapabilities(
  capabilities: TrustedDeviceCapabilities,
): void {
  if (capabilities.webCrypto && capabilities.indexedDb) return;

  const missing: string[] = [];

  if (!capabilities.webCrypto) {
    missing.push("Web Crypto");
  }

  if (!capabilities.indexedDb) {
    missing.push("IndexedDB");
  }

  throw new TrustedDevicePreparationError(
    `Trusted-device login is unavailable: ${missing.join(" and ")} ${
      missing.length > 1 ? "are" : "is"
    } blocked or unsupported (secureContext: ${capabilities.secureContext})`,
    "trustedDeviceCapability",
  );
}

/**
 * Bound how long a preparation may hang before the caller regains control.
 *
 * @remarks
 * An IndexedDB open request that is blocked by another tab never fires
 * `success` or `error`, which would otherwise leave the submit button spinning
 * forever. The rejection keeps the trusted-device requirement intact — the
 * login is abandoned rather than sent without a proof.
 *
 * @example
 * const proof = await withTrustedDeviceTimeout(createProof(), 8_000);
 */
export async function withTrustedDeviceTimeout<TResult>(
  operation: Promise<TResult>,
  timeoutMs: number,
  phase: TrustedDeviceTimeoutPhase = "local",
): Promise<TResult> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const expiration = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(createTrustedDeviceTimeoutError(timeoutMs, phase));
    }, timeoutMs);
  });

  try {
    return await Promise.race([operation, expiration]);
  } finally {
    clearTimeout(timeoutId);
  }
}

function createTrustedDeviceTimeoutError(
  timeoutMs: number,
  phase: TrustedDeviceTimeoutPhase,
) {
  const timeoutError = new Error(
    `Trusted-device ${phase} preparation timed out after ${timeoutMs}ms`,
  );

  timeoutError.name = TRUSTED_DEVICE_TIMEOUT_NAME;
  (timeoutError as TrustedDeviceTimeoutError).phase = phase;

  return timeoutError;
}

/**
 * Budget that follows the preparation through its phases.
 *
 * @remarks
 * Only the active phase's timer runs, so slow backend time is never charged to
 * the local budget (and vice versa). Starts in the `local` phase.
 *
 * @example
 * const guard = createTrustedDevicePhaseGuard({ local: 8_000, challenge: 25_000 });
 * guard.enter("challenge");
 * await Promise.race([proof, guard.expiration]);
 * guard.stop();
 */
export function createTrustedDevicePhaseGuard(
  budgets: Record<TrustedDeviceTimeoutPhase, number>,
) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let rejectExpiration: (error: Error) => void = () => undefined;

  const expiration = new Promise<never>((_resolve, reject) => {
    rejectExpiration = reject;
  });

  // The caller races this promise; never let a lost race surface as unhandled.
  expiration.catch(() => undefined);

  const enter = (phase: TrustedDeviceTimeoutPhase) => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      rejectExpiration(createTrustedDeviceTimeoutError(budgets[phase], phase));
    }, budgets[phase]);
  };

  enter("local");

  return {
    expiration,
    enter,
    stop: () => clearTimeout(timeoutId),
  };
}

/**
 * Report which phase a bounded-wait expiry happened in, if it is one.
 *
 * @example
 * const phase = getTrustedDeviceTimeoutPhase(error); // "challenge" | "local" | undefined
 */
export function getTrustedDeviceTimeoutPhase(
  error: unknown,
): TrustedDeviceTimeoutPhase | undefined {
  let candidate: unknown = error;

  for (let depth = 0; depth <= MAX_ERROR_CAUSE_DEPTH; depth += 1) {
    if (!(candidate instanceof Error)) return undefined;

    if (candidate.name === TRUSTED_DEVICE_TIMEOUT_NAME) {
      return (candidate as TrustedDeviceTimeoutError).phase;
    }

    candidate = candidate.cause;
  }

  return undefined;
}

/**
 * Tell a bounded-wait expiry apart from a real browser or backend failure.
 *
 * @remarks
 * The preparation hook re-throws an untagged failure wrapped in a
 * {@link TrustedDevicePreparationError}, so the expiry marker is looked up
 * along the `cause` chain — a login caller only ever sees the wrapper. The walk
 * is depth-bounded so a self-referencing cause cannot spin.
 *
 * @example
 * logLoginDiagnostic("failed", { timedOut: isTrustedDeviceTimeoutError(error) });
 */
export function isTrustedDeviceTimeoutError(error: unknown): boolean {
  let candidate: unknown = error;

  for (let depth = 0; depth <= MAX_ERROR_CAUSE_DEPTH; depth += 1) {
    if (!(candidate instanceof Error)) return false;

    if (candidate.name === TRUSTED_DEVICE_TIMEOUT_NAME) return true;

    candidate = candidate.cause;
  }

  return false;
}

/**
 * Return the stage a trusted-device failure carries, or nothing when unrelated.
 *
 * @example
 * const stage = getTrustedDevicePreparationStage(error) ?? "loginAction";
 */
export function getTrustedDevicePreparationStage(
  error: unknown,
): LoginDiagnosticStage | undefined {
  if (error instanceof TrustedDevicePreparationError) return error.stage;

  return undefined;
}

/**
 * Attribute an untagged proof failure to a stage using the challenge progress.
 *
 * @remarks
 * The proof service is a shared browser primitive that throws plain errors, so
 * how far the challenge round-trip got is what separates a local database or
 * key-generation fault from a signing fault.
 *
 * @example
 * const stage = classifyTrustedDeviceFailure(error, challengeState);
 */
export function classifyTrustedDeviceFailure(
  error: unknown,
  challengeState: TrustedDeviceChallengeState,
): LoginDiagnosticStage {
  const taggedStage = getTrustedDevicePreparationStage(error);

  if (taggedStage) return taggedStage;

  // A tagged local failure names its own stage, regardless of how far the
  // challenge got (key generation used to be misreported as "database").
  switch (getBrowserTrustedDeviceFailureKind(error)) {
    case "keyUnsupported":
      return "trustedDeviceCapability";
    case "signingFailed":
      return "trustedDeviceSignature";
    case "storageUnavailable":
    case "keyNotStorable":
    case "storageQuota":
    case "storageBlocked":
    case "notEnrolled":
      return "trustedDeviceDatabase";
    default:
      break;
  }

  if (challengeState === "requested") return "trustedDeviceChallenge";

  if (challengeState === "completed") return "trustedDeviceSignature";

  return "trustedDeviceDatabase";
}
